import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Avatar,
  Button,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Popover,
  Stack,
  TextField,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  DirectionsCar as CarIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  LocalGasStation as FuelIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  CalendarMonth as CalendarMonthIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { usePeriod } from '../../context/PeriodContext';

const Navbar = () => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [periodAnchor, setPeriodAnchor] = useState<null | HTMLElement>(null);

  const { user, logout, hasPermission } = useAuth();
  const { startDate, endDate, setPeriod, label: periodLabel } = usePeriod();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleOpenPeriod = (event: React.MouseEvent<HTMLElement>) => {
    setPeriodAnchor(event.currentTarget);
  };

  const handleClosePeriod = () => {
    setPeriodAnchor(null);
  };

  const applyPreset = (preset: string) => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (preset) {
      case 'Ce mois':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'Mois dernier':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'Ce trimestre':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        end = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'Cette année':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setPeriod(start.toISOString().split('T')[0], end.toISOString().split('T')[0], preset);
    handleClosePeriod();
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    logout();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Définition des éléments de navigation
  const navigationItems = [
    { name: 'Tableau de bord', path: '/', icon: <DashboardIcon />, permission: 'dashboard' },
    { name: 'Véhicules', path: '/vehicules', icon: '/vehicule-icon.png', permission: 'vehicles' },
    { name: 'Chauffeurs', path: '/chauffeurs', icon: '/chauffeur-icon.png', permission: 'drivers' },
    { name: 'Affectations', path: '/assignments', icon: '/affectation-icon.png', permission: 'assignments' },
    { name: 'Cartes', path: '/cartes-carburant', icon: '/carburant-icon.png', permission: 'fuelCards' },
    { name: 'Maintenance', path: '/garage', icon: '/garage-icon.png', permission: 'vehicles' },
    { name: 'Sinistres', path: '/amendes-degats', icon: '/sinistre-icon.png', permission: 'damages' },
  ];

  // Filtrer les items selon les permissions
  const filteredNavItems = navigationItems.filter(item => 
    user?.isSuperAdmin || hasPermission(item.permission as any, 'read')
  );

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Box component="img" src="/logo.png" alt="Logo" sx={{ height: 40 }} />
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold', 
            fontSize: '0.75rem',
            fontFamily: '"Bauhaus 93", "Orbitron", sans-serif',
          }}
        >
          Master<span style={{ color: '#64B5F6' }}>D</span>i Fleet
        </Typography>
      </Box>
      <Divider />
      <List>
        {filteredNavItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              selected={location.pathname === item.path}
              sx={{
                textAlign: 'left',
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                  '&:hover': {
                    bgcolor: 'primary.main',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: location.pathname === item.path ? 'inherit' : 'text.secondary' }}>
                {typeof item.icon === 'string' ? (
                  <Box 
                    component="img" 
                    src={item.icon} 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      filter: location.pathname === item.path 
                        ? 'brightness(0) saturate(100%) invert(40%) sepia(100%) saturate(2000%) hue-rotate(200deg)' // Bleu quand sélectionné
                        : 'brightness(0) saturate(100%) invert(30%)' // Gris foncé par défaut
                    }} 
                  />
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <CalendarMonthIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary={`Période : ${periodLabel}`} />
        </ListItem>
        <Box sx={{ px: 2, pb: 1 }}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
            {['Ce mois', 'Mois dernier', 'Ce trimestre', 'Cette année'].map((preset) => (
              <Chip
                key={preset}
                label={preset}
                onClick={() => applyPreset(preset)}
                color={periodLabel === preset ? 'primary' : 'default'}
                variant={periodLabel === preset ? 'filled' : 'outlined'}
                size="small"
                sx={{ mb: 0.5 }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              type="date"
              label="Début"
              size="small"
              value={startDate}
              onChange={(e) => setPeriod(e.target.value, endDate)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="date"
              label="Fin"
              size="small"
              value={endDate}
              onChange={(e) => setPeriod(startDate, e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText primary="Déconnexion" sx={{ color: 'error.main' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'white', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {/* Menu Mobile (Burger) - Toujours visible sur mobile */}
            <IconButton
              size="large"
              aria-label="menu de navigation"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleDrawerToggle}
              color="inherit"
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                mr: 1
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo Mobile */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                flexGrow: 1,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                sx={{ height: 32, mb: 0.5 }}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontFamily: '"Bauhaus 93", "Orbitron", sans-serif',
                  fontWeight: 700,
                  letterSpacing: '.1rem',
                  color: 'inherit',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                MASTER<span style={{ color: '#64B5F6' }}>D</span>I FLEET
              </Typography>
            </Box>

            {/* Logo Desktop */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                alignItems: 'center',
                textDecoration: 'none',
                mr: 2,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                sx={{ height: 40, mb: 0.5 }}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontFamily: '"Bauhaus 93", "Orbitron", sans-serif',
                  fontWeight: 700,
                  letterSpacing: '.1rem',
                  color: 'inherit',
                  fontSize: '0.75rem',
                }}
              >
                MASTER<span style={{ color: '#64B5F6' }}>D</span>I FLEET
              </Typography>
            </Box>

            {/* Liens Desktop - Cachés sur mobile */}
            <Box sx={{ 
              flexGrow: 1, 
              display: { xs: 'none', md: 'flex' }, 
              gap: { md: 0.5, lg: 1 },
              flexWrap: 'wrap',
              justifyContent: 'flex-start'
            }}>
              {filteredNavItems.map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  startIcon={
                    typeof item.icon === 'string' ? (
                      <Box 
                        component="img" 
                        src={item.icon} 
                        sx={{ 
                          width: 20, 
                          height: 20, 
                          filter: location.pathname === item.path 
                            ? 'brightness(0) saturate(100%) invert(40%) sepia(100%) saturate(2000%) hue-rotate(200deg)' // Bleu quand sélectionné
                            : 'brightness(0) saturate(100%) invert(30%)' // Gris foncé par défaut
                        }} 
                      />
                    ) : (
                      item.icon
                    )
                  }
                  sx={{ 
                    my: 2, 
                    color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                    display: 'flex',
                    fontWeight: location.pathname === item.path ? 700 : 400,
                    bgcolor: location.pathname === item.path ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    fontSize: { md: '0.875rem', lg: '1rem' },
                    px: { md: 1, lg: 1.5 },
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.12)',
                    }
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Box>

            {/* Period Selector */}
            <Tooltip title="Choisir la période">
              <Button
                onClick={handleOpenPeriod}
                startIcon={<CalendarMonthIcon />}
                variant="outlined"
                size="small"
                sx={{
                  mr: 2,
                  textTransform: 'none',
                  borderRadius: 2,
                  display: { xs: 'none', md: 'inline-flex' },
                  whiteSpace: 'nowrap',
                  fontSize: '0.8rem',
                }}
              >
                {periodLabel}
              </Button>
            </Tooltip>
            <Popover
              open={Boolean(periodAnchor)}
              anchorEl={periodAnchor}
              onClose={handleClosePeriod}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { p: 2, minWidth: 280, borderRadius: 3 } } }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Période
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {['Ce mois', 'Mois dernier', 'Ce trimestre', 'Cette année'].map((preset) => (
                  <Chip
                    key={preset}
                    label={preset}
                    onClick={() => applyPreset(preset)}
                    color={periodLabel === preset ? 'primary' : 'default'}
                    variant={periodLabel === preset ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Plage personnalisée
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  type="date"
                  label="Début"
                  size="small"
                  value={startDate}
                  onChange={(e) => setPeriod(e.target.value, endDate)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="date"
                  label="Fin"
                  size="small"
                  value={endDate}
                  onChange={(e) => setPeriod(startDate, e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Popover>

            {/* Menu Utilisateur */}
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Ouvrir les paramètres">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    {user?.firstName?.charAt(0) || user?.username?.charAt(0) || 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.role}
                  </Typography>
                </Box>
                <Divider />
                
                {user?.isSuperAdmin && (
                  <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/super-admin/users'); }}>
                    <ListItemIcon>
                      <AdminIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Administration</ListItemText>
                  </MenuItem>
                )}
                
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>Déconnexion</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Drawer Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: { xs: 280, sm: 300 },
            pt: 2
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;





